import React from 'react';
import { PRICING } from '../pricing';
import StorageIcon from '@mui/icons-material/Storage';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SpeedIcon from '@mui/icons-material/Speed';
import QuizIcon from '@mui/icons-material/Quiz';
import LanIcon from '@mui/icons-material/Lan';
import CalculateIcon from '@mui/icons-material/Calculate';

export const systemDesign = {
    title: "System Design",
    subtitle: "Architect Level",
    badge: "BESTSELLER",
    popular: true,
    icon: <StorageIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
    description: "Architect scalable, distributed high-level systems.",
    features: [
      "HLD & LLD Real-World Scenarios",
      "Capacity Estimation Models",
      "Scalability & Replication Patterns",
      "FAANG Architecture Case Studies"
    ],
    price: PRICING.systemDesign.price,
    mrp: PRICING.systemDesign.mrp,
    topics: [
      {
        id: "hld_fundamentals",
        title: "HLD Fundamentals",
        desc: "Foundational concepts for building scalable, high-performance systems.",
        badge: "Core",
        icon: <CalculateIcon />,
        group: "HLD Basics",
        problems: [
          { id: "hld_basic_1", title: "HLD Basics: Three Pillars", badge: "Core", fileUrl: "/documents/hld/hld_basics.md" },
          { id: "hld_tradeoffs", title: "The Scale of Trade-offs", badge: "Consult", fileUrl: "/documents/hld/hld_tradeoffs.md" },
          { id: "hld_distributed_intro", title: "Intro to Distributed Systems", badge: "Logic", fileUrl: "/documents/hld/hld_distributed_intro.md" },
          { id: "hld_scalability", title: "Scalability Strategies", badge: "Scale", fileUrl: "/documents/hld/hld_scalability.md" },
          { id: "hld_availability", title: "Availability & Reliability", badge: "SRE", fileUrl: "/documents/hld/hld_availability.md" },
          { id: "hld_capacity", title: "Capacity Estimation Models", badge: "Math", fileUrl: "/documents/hld/hld_capacity.md" },
          { id: "hld_scaling_millions", title: "Scaling to Millions of Users", badge: "Scale", fileUrl: "/documents/hld/hld_scaling_millions.md" }
        ]
      },
      {
        id: "hld_networking",
        title: "Networking & Traffic Management",
        desc: "Master global traffic routing, load balancing, and delivery networks.",
        badge: "Network",
        icon: <LanIcon />,
        group: "Networking",
        problems: [
          { id: "hld_dns", title: "DNS: The Global Phonebook", badge: "Network", fileUrl: "/documents/hld/hld_dns.md" },
          { id: "hld_load_balancers", title: "Load Balancers & Algorithms", badge: "Proxy", fileUrl: "/documents/hld/hld_load_balancers.md" },
          { id: "hld_cdn", title: "Content Delivery Networks (CDN)", badge: "Edge", fileUrl: "/documents/hld/hld_cdn.md" },
          { id: "hld_api_gateway", title: "API Gateway & BFF Pattern", badge: "API", fileUrl: "/documents/hld/hld_api_gateway.md" },
          { id: "hld_proxies", title: "Forward vs Reverse Proxies", badge: "Proxy", fileUrl: "/documents/hld/hld_proxies.md" },
          { id: "hld_communication", title: "Communication Protocols (gRPC/Websockets)", badge: "Network", fileUrl: "/documents/hld/hld_communication.md" },
          { id: "hld_service_mesh", title: "Service Mesh Architecture", badge: "Cloud", fileUrl: "/documents/hld/hld_service_mesh.md" }
        ]
      },
      {
        id: "hld_data_layer",
        title: "The Data Layer",
        desc: "Deep dive into sharding, replication, indexing, and object storage.",
        badge: "Data",
        icon: <StorageIcon />,
        group: "Data Layer",
        problems: [
          { id: "hld_database_basics", title: "Database Fundamentals", badge: "SQL", fileUrl: "/documents/hld/hld_database_basics.md" },
          { id: "hld_sharding", title: "Sharding & Partitioning", badge: "Scale", fileUrl: "/documents/hld/hld_sharding.md" },
          { id: "hld_replication", title: "Replication & Consistency", badge: "HA", fileUrl: "/documents/hld/hld_replication.md" },
          { id: "hld_indexing", title: "High-Performance Indexing", badge: "Perf", fileUrl: "/documents/hld/hld_indexing.md" },
          { id: "hld_caching", title: "Distributed Caching (Redis)", badge: "Speed", fileUrl: "/documents/hld/hld_caching.md" },
          { id: "hld_object_storage", title: "Object Storage (S3 Internal Arch)", badge: "Cloud", fileUrl: "/documents/hld/hld_object_storage.md" },
          { id: "hld_db_concepts", title: "NoSQL Architectures", badge: "NoSQL", fileUrl: "/documents/hld/hld_db_concepts.md" },
          { id: "hld_search_index", title: "Search Indexing (Elasticsearch)", badge: "Search", fileUrl: "/documents/hld/hld_search_index.md" }
        ]
      },
      {
        id: "hld_distributed_patterns",
        title: "Advanced Distributed Patterns",
        desc: "Master coordination, consensus, and transactional integrity at scale.",
        badge: "Expert",
        icon: <SpeedIcon />,
        group: "Distributed Patterns",
        problems: [
          { id: "hld_cap_hashing", title: "CAP Theorem & Consistent Hashing", badge: "Core", fileUrl: "/documents/hld/hld_cap_hashing.md" },
          { id: "hld_consensus", title: "Consensus Algorithms (Raft/Paxos)", badge: "Paxos", fileUrl: "/documents/hld/hld_consensus.md" },
          { id: "hld_id_generator", title: "Distributed Unique ID Generation", badge: "Hard", fileUrl: "/documents/hld/hld_id_generator.md" },
          { id: "hld_idempotency", title: "Idempotency & Retry Strategies", badge: "Logic", fileUrl: "/documents/hld/hld_idempotency.md" },
          { id: "hld_dist_transactions", title: "Distributed Transactions (Saga)", badge: "Saga", fileUrl: "/documents/hld/hld_dist_transactions.md" },
          { id: "hld_queues", title: "Message Queues (RabbitMQ/SQS)", badge: "Async", fileUrl: "/documents/hld/hld_queues.md" },
          { id: "hld_event_streaming", title: "Event Streaming (Kafka Architecture)", badge: "Stream", fileUrl: "/documents/hld/hld_event_streaming.md" },
          { id: "hld_observability", title: "Observability: Logs, Metrics, Traces", badge: "SRE", fileUrl: "/documents/hld/hld_observability.md" },
          { id: "hld_probabilistic_ds", title: "Probabilistic Data Structures", badge: "Math", fileUrl: "/documents/hld/hld_probabilistic_ds.md" },
          { id: "hld_realtime", title: "Real-time Systems Design", badge: "Live", fileUrl: "/documents/hld/hld_realtime.md" }
        ]
      },
      {
        id: "hld_case_studies",
        title: "FAANG Case Studies",
        desc: "Step-by-step architectural deep dives into global platforms.",
        badge: "Practice",
        icon: <DashboardIcon />,
        group: "Case Studies",
        problems: [
          { id: "hld_tinyurl", title: "Design TinyURL (Shortening Service)", badge: "Easy", fileUrl: "/documents/hld/hld_tinyurl.md" },
          { id: "hld_whatsapp", title: "Design WhatsApp (Global Chat)", badge: "Hard", fileUrl: "/documents/hld/hld_whatsapp.md" },
          { id: "hld_youtube", title: "Design YouTube (Video Streaming)", badge: "Hard", fileUrl: "/documents/hld/hld_youtube.md" },
          { id: "hld_uber", title: "Design Uber (Real-time Tracking)", badge: "Hard", fileUrl: "/documents/hld/hld_uber.md" },
          { id: "hld_amazon", title: "Design Amazon (E-Commerce Arch)", badge: "Hard", fileUrl: "/documents/hld/hld_amazon.md" },
          { id: "hld_dropbox", title: "Design Dropbox (File Sync Service)", badge: "Hard", fileUrl: "/documents/hld/hld_dropbox.md" },
          { id: "hld_google_docs", title: "Design Google Docs (Collaborative)", badge: "Expert", fileUrl: "/documents/hld/hld_google_docs.md" },
          { id: "hld_ticketmaster", title: "Design Ticketmaster (Booking)", badge: "Expert", fileUrl: "/documents/hld/hld_ticketmaster.md" },
          { id: "hld_news_feed", title: "Design News Feed (FB/Twitter)", badge: "Hard", fileUrl: "/documents/hld/hld_news_feed.md" },
          { id: "hld_notifications", title: "Design a Notification Service", badge: "Medium", fileUrl: "/documents/hld/hld_notifications.md" },
          { id: "hld_metrics_aggregator", title: "Design Metrics Aggregator", badge: "Expert", fileUrl: "/documents/hld/hld_metrics_aggregator.md" },
          { id: "hld_proximity", title: "Design Proximity Service (Yelp)", badge: "Expert", fileUrl: "/documents/hld/hld_proximity.md" },
          { id: "hld_leaderboard", title: "Design a Global Leaderboard", badge: "Medium", fileUrl: "/documents/hld/hld_leaderboard.md" },
          { id: "hld_web_crawler", title: "Design a Web Crawler (Google Search)", badge: "Expert", fileUrl: "/documents/hld/hld_web_crawler.md" }
        ]
      },
      {
        id: "hld_quizzes",
        title: "Architecture Quizzes",
        desc: "Test your mastery of backend system design & architecture.",
        badge: "Quiz",
        icon: <QuizIcon />,
        group: "Final Assessment",
        problems: [
          { id: "quiz_hld_basics", title: "HLD Basics & Fundamentals Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_hld_basics.md" },
          { id: "quiz_hld_advance", title: "Advanced Distributed Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_hld_advance.md" },
          { id: "quiz_hld_case_studies", title: "System Design Case Studies Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_hld_case_studies.md" }
        ]
      }
    ]
  };
